import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Plus, Trash2, ArrowLeft, ArrowRight, Save, Upload, Loader2 } from 'lucide-react';
import { toast } from '../hooks/use-toast';
import { dossiersAPI, cvAPI, templatesAPI } from '../api/api';
import AppLayout from '../components/AppLayout';

// ─── Helpers ─────────────────────────────────────────────────────────────────
// Convert domain-grouped competences (array) to editable text format
function competencesToText(raw) {
  if (!raw) return '';
  if (typeof raw === 'string') return raw;
  if (Array.isArray(raw)) {
    return raw.map(group => {
      if (group && typeof group === 'object' && group.domain) {
        return `${group.domain}: ${(group.tags || []).join(', ')}`;
      }
      return String(group);
    }).join('\n');
  }
  return String(raw);
}

// ─── Form CSS ─────────────────────────────────────────────────────────────────
const FORM_CSS = `
.df-progress-bar {
  background: var(--surface);
  border-bottom: 1px solid var(--border);
  padding: 16px 24px;
  flex-shrink: 0;
}
.df-steps {
  display: flex;
  justify-content: space-between;
  margin-bottom: 10px;
}
.df-step-label {
  font-size: 11px;
  font-weight: 600;
  color: var(--muted);
  cursor: default;
  transition: color 0.15s;
}
.df-step-label.active { color: var(--vio2); }
.df-step-label.done { color: var(--muted2); }
.df-progress-track {
  height: 3px;
  background: rgba(255,255,255,0.06);
  border-radius: 3px;
  overflow: hidden;
}
.df-progress-fill {
  height: 100%;
  border-radius: 3px;
  background: linear-gradient(90deg, var(--vio), var(--vio2));
  transition: width 0.35s ease;
}
.df-body {
  flex: 1;
  overflow-y: auto;
  padding: 24px;
}
.df-upload-box {
  background: rgba(37,99,235,0.07);
  border: 1px solid rgba(37,99,235,0.25);
  border-radius: 10px;
  padding: 16px 18px;
  margin-bottom: 20px;
}
.df-upload-title {
  display: flex;
  align-items: center;
  gap: 7px;
  font-size: 13px;
  font-weight: 600;
  color: #93C5FD;
  margin-bottom: 4px;
}
.df-upload-desc {
  font-size: 12px;
  color: #60A5FA;
  margin-bottom: 12px;
}
.df-upload-row {
  display: flex;
  gap: 10px;
  align-items: center;
}
.df-file-input {
  flex: 1;
  background: rgba(0,0,0,0.3);
  border: 1px solid var(--border2);
  border-radius: 7px;
  padding: 7px 10px;
  color: var(--muted2);
  font-size: 12px;
  font-family: inherit;
  outline: none;
}
.df-file-input::file-selector-button {
  background: var(--surface2);
  border: 1px solid var(--border2);
  color: var(--muted2);
  border-radius: 5px;
  padding: 3px 9px;
  font-size: 11px;
  cursor: pointer;
  font-family: inherit;
  margin-right: 8px;
}
.df-upload-hint { font-size: 11px; color: var(--muted); margin-top: 8px; }
.df-card {
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 10px;
  overflow: hidden;
  margin-bottom: 20px;
}
.df-card-head {
  padding: 16px 20px;
  border-bottom: 1px solid var(--border);
}
.df-card-title { font-size: 14px; font-weight: 600; color: var(--white); margin-bottom: 2px; }
.df-card-desc { font-size: 12px; color: var(--muted2); }
.df-card-body { padding: 20px; }
.df-section-title {
  font-size: 12px;
  font-weight: 600;
  color: var(--muted2);
  text-transform: uppercase;
  letter-spacing: 0.6px;
  margin-bottom: 14px;
  margin-top: 20px;
  display: flex;
  align-items: center;
  gap: 8px;
}
.df-section-title::before { content: ''; display: block; flex: 0 0 2px; width: 2px; height: 13px; background: var(--vio); border-radius: 2px; }
.df-section-title:first-child { margin-top: 0; }
.df-grid-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; }
.df-grid-3 { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 14px; }
@media (max-width: 640px) { .df-grid-2, .df-grid-3 { grid-template-columns: 1fr; } }
.df-field { display: flex; flex-direction: column; gap: 5px; margin-bottom: 14px; }
.df-field:last-child { margin-bottom: 0; }
.df-label {
  font-size: 11.5px;
  font-weight: 600;
  color: var(--muted2);
  letter-spacing: 0.1px;
}
.df-label-hint { font-size: 10.5px; color: var(--muted); font-weight: 400; margin-left: 4px; }
.df-input {
  background: var(--bg);
  border: 1px solid var(--border2);
  border-radius: 7px;
  padding: 8px 12px;
  color: var(--white);
  font-size: 13px;
  font-family: inherit;
  outline: none;
  transition: border-color 0.15s;
  width: 100%;
}
.df-input::placeholder { color: var(--muted); }
.df-input:focus { border-color: rgba(123,104,238,0.55); }
.df-textarea {
  background: var(--bg);
  border: 1px solid var(--border2);
  border-radius: 7px;
  padding: 9px 12px;
  color: var(--white);
  font-size: 13px;
  font-family: inherit;
  outline: none;
  resize: vertical;
  min-height: 80px;
  width: 100%;
  transition: border-color 0.15s;
  line-height: 1.55;
}
.df-textarea::placeholder { color: var(--muted); }
.df-textarea:focus { border-color: rgba(123,104,238,0.55); }
.df-textarea.mono { font-family: 'JetBrains Mono', 'Fira Code', Consolas, monospace; font-size: 12px; }
.df-select {
  background: var(--bg);
  border: 1px solid var(--border2);
  border-radius: 7px;
  padding: 8px 30px 8px 12px;
  color: var(--white);
  font-size: 13px;
  font-family: inherit;
  outline: none;
  cursor: pointer;
  appearance: none;
  -webkit-appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%235A5580' d='M6 8L1 3h10z'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 10px center;
  width: 100%;
  transition: border-color 0.15s;
}
.df-select:focus { border-color: rgba(123,104,238,0.55); }
.df-select option { background: var(--surface2); color: var(--white); }
.df-sub-card {
  background: var(--surface2);
  border: 1px solid var(--border);
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 14px;
  position: relative;
}
.df-sub-card-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 14px;
}
.df-sub-card-label {
  font-size: 12px;
  font-weight: 600;
  color: var(--muted2);
}
.df-btn {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 8px 16px;
  border-radius: 7px;
  font-size: 12.5px;
  font-weight: 600;
  cursor: pointer;
  border: none;
  transition: all 0.12s;
  font-family: inherit;
  white-space: nowrap;
}
.df-btn-primary { background: var(--vio); color: white; }
.df-btn-primary:hover { background: #8B7CF5; box-shadow: 0 4px 14px rgba(123,104,238,0.25); }
.df-btn-primary:disabled { opacity: 0.55; cursor: not-allowed; box-shadow: none; }
.df-btn-ghost {
  background: transparent;
  color: var(--muted2);
  border: 1px solid var(--border2);
}
.df-btn-ghost:hover { background: rgba(255,255,255,0.04); color: var(--white); }
.df-btn-ghost:disabled { opacity: 0.4; cursor: not-allowed; }
.df-btn-danger {
  background: transparent;
  color: #F87171;
  border: 1px solid rgba(239,68,68,0.2);
  padding: 5px 9px;
  font-size: 11.5px;
}
.df-btn-danger:hover { background: rgba(239,68,68,0.07); border-color: rgba(239,68,68,0.35); }
.df-btn-add {
  width: 100%;
  background: transparent;
  border: 1.5px dashed rgba(123,104,238,0.2);
  color: var(--muted2);
  border-radius: 8px;
  padding: 10px;
  font-size: 12.5px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.12s;
  font-family: inherit;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  margin-top: 6px;
}
.df-btn-add:hover { border-color: rgba(123,104,238,0.45); color: var(--white); background: rgba(123,104,238,0.04); }
.df-btn-save {
  background: #166534;
  color: #4ADE80;
  border: 1px solid rgba(74,222,128,0.2);
}
.df-btn-save:hover { background: #15803D; box-shadow: 0 4px 14px rgba(34,197,94,0.15); }
.df-info-box {
  background: rgba(37,99,235,0.06);
  border: 1px solid rgba(37,99,235,0.2);
  border-radius: 7px;
  padding: 10px 13px;
  font-size: 12px;
  color: #93C5FD;
  margin-top: 12px;
}
.df-nav {
  display: flex;
  justify-content: space-between;
  padding: 16px 24px;
  border-top: 1px solid var(--border);
  background: var(--surface);
  flex-shrink: 0;
}
.df-tmpl-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; }
@media (max-width: 500px) { .df-tmpl-grid { grid-template-columns: 1fr; } }
.df-tmpl-card {
  position: relative;
  cursor: pointer;
  border-radius: 9px;
  border: 1.5px solid var(--border2);
  background: rgba(255,255,255,0.02);
  padding: 14px;
  transition: border-color 0.15s, background 0.15s;
  display: flex;
  align-items: center;
  gap: 12px;
}
.df-tmpl-card:hover { border-color: rgba(123,104,238,0.4); background: rgba(123,104,238,0.04); }
.df-tmpl-card.selected { border-color: var(--vio); background: rgba(123,104,238,0.08); }
.df-tmpl-card.disabled { opacity: 0.45; cursor: not-allowed; border-style: dashed; }
.df-tmpl-card.disabled:hover { border-color: var(--border2); background: rgba(255,255,255,0.02); }
.df-tmpl-thumb { width: 36px; height: 46px; border-radius: 5px; flex-shrink: 0; }
.df-tmpl-name { font-size: 12.5px; font-weight: 600; color: var(--white); }
.df-tmpl-desc { font-size: 11px; color: var(--muted2); margin-top: 2px; }
.df-tmpl-check {
  position: absolute;
  top: 8px; right: 8px;
  width: 18px; height: 18px;
  background: var(--vio);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}
.df-tmpl-badge {
  position: absolute;
  top: 7px; right: 8px;
  font-size: 9.5px;
  color: var(--muted);
  background: rgba(255,255,255,0.05);
  padding: 2px 6px;
  border-radius: 10px;
}
.df-wrap {
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
}
`;

const DossierForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const totalSteps = 4;
  const [uploadingCV, setUploadingCV] = useState(false);

  // For new dossiers, allow pre-selecting a template via ?template=<id> query
  // param (used by the sidebar template-gallery shortcut).
  const initialTemplate = (() => {
    if (typeof window === 'undefined') return 'signal_azure';
    try {
      const t = new URLSearchParams(window.location.search).get('template');
      return t || 'signal_azure';
    } catch {
      return 'signal_azure';
    }
  })();

  // Form state
  const [consultantName, setConsultantName] = useState('');
  const [consultantTitle, setConsultantTitle] = useState('');
  const [nameFontSize, setNameFontSize] = useState('36');
  const [titleFontSize, setTitleFontSize] = useState('24');
  const [fontFamily, setFontFamily] = useState('Helvetica-Bold');
  const [templateId, setTemplateId] = useState(initialTemplate);
  const [templates, setTemplates] = useState([]);
  const [competences, setCompetences] = useState('');
  const [formations, setFormations] = useState([{ id: Date.now(), title: '', institution: '', year: '', description: '' }]);
  const [experiences, setExperiences] = useState([{ id: Date.now(), poste: '', entreprise: '', duree: '', mission: '', contexte: '', environnement: '' }]);

  useEffect(() => {
    if (id && id !== 'new') {
      // Load existing dossier for editing
      const loadDossier = async () => {
        try {
          const dossier = await dossiersAPI.getOne(id);
          setConsultantName(dossier.consultant_name || '');
          setConsultantTitle(dossier.consultant_title || '');
          setNameFontSize(dossier.name_font_size || '36');
          setTitleFontSize(dossier.title_font_size || '24');
          setFontFamily(dossier.font_family || 'Helvetica-Bold');
          setCompetences(competencesToText(dossier.competences));
          
          // Add unique IDs to formations if they don't have one
          const formationsWithIds = (dossier.formations || []).map((f, index) => ({
            ...f,
            id: f.id || Date.now() + index
          }));
          setFormations(formationsWithIds.length > 0 ? formationsWithIds : [{ id: Date.now(), title: '', institution: '', year: '', description: '' }]);
          
          // Add unique IDs to experiences if they don't have one
          const experiencesWithIds = (dossier.experiences || []).map((e, index) => ({
            ...e,
            id: e.id || Date.now() + index + 1000  // Offset to avoid collision with formations
          }));
          setExperiences(experiencesWithIds.length > 0 ? experiencesWithIds : [{ id: Date.now(), poste: '', entreprise: '', duree: '', mission: '', contexte: '', environnement: '' }]);
          setTemplateId(dossier.template_id || 'signal_azure');
        } catch (error) {
          console.error('Error loading dossier:', error);
          toast({
            title: 'Erreur',
            description: 'Impossible de charger le dossier',
            variant: 'destructive',
          });
          navigate('/dashboard');
        }
      };
      loadDossier();
    }
  }, [id, navigate]);

  useEffect(() => {
    templatesAPI.getAll()
      .then(data => setTemplates(data || []))
      .catch(() => setTemplates([]));
  }, []);

  useEffect(() => {
    const el = document.getElementById('df-form-styles');
    if (!el) {
      const style = document.createElement('style');
      style.id = 'df-form-styles';
      style.textContent = FORM_CSS;
      document.head.appendChild(style);
    }
    return () => {};
  }, []);

  // Competences handlers - removed, now it's just a string

  // Formations handlers
  const addFormation = () => {
    setFormations([...formations, { id: Date.now(), title: '', institution: '', year: '', description: '' }]);
  };

  const removeFormation = (formationId) => {
    if (formations.length > 1) {
      setFormations(formations.filter(f => f.id !== formationId));
    }
  };

  const updateFormation = (formationId, field, value) => {
    setFormations(formations.map(f => {
      if (f.id === formationId) {
        return { ...f, [field]: value };
      }
      return f;
    }));
  };

  // Experiences handlers
  const addExperience = () => {
    setExperiences([...experiences, { id: Date.now(), poste: '', entreprise: '', duree: '', mission: '', contexte: '', environnement: '' }]);
  };

  const removeExperience = (experienceId) => {
    if (experiences.length > 1) {
      setExperiences(experiences.filter(e => e.id !== experienceId));
    }
  };

  const updateExperience = (experienceId, field, value) => {
    setExperiences(experiences.map(e => {
      if (e.id === experienceId) {
        return { ...e, [field]: value };
      }
      return e;
    }));
  };

  const handleNext = () => {
    if (step === 1 && (!consultantName || !consultantTitle)) {
      toast({
        title: 'Champs requis',
        description: 'Veuillez remplir tous les champs',
        variant: 'destructive',
      });
      return;
    }
    if (step < totalSteps) {
      setStep(step + 1);
    }
  };

  const handlePrevious = () => {
    if (step > 1) {
      setStep(step - 1);
    }
  };

  const handleSave = async () => {
    const dossierData = {
      consultant_name: consultantName,
      consultant_title: consultantTitle,
      name: consultantName || 'Sans titre',
      name_font_size: nameFontSize,
      title_font_size: titleFontSize,
      font_family: fontFamily,
      template_id: templateId,
      competences,
      formations: formations.filter(f => f.title),
      experiences: experiences.filter(e => e.poste)
    };

    try {
      if (id === 'new') {
        await dossiersAPI.create(dossierData);
        toast({
          title: 'Dossier créé',
          description: 'Le dossier a été créé avec succès',
        });
      } else {
        await dossiersAPI.update(id, dossierData);
        toast({
          title: 'Dossier modifié',
          description: 'Le dossier a été modifié avec succès',
        });
      }
      navigate('/dashboard');
    } catch (error) {
      console.error('Error saving dossier:', error);
      toast({
        title: 'Erreur',
        description: 'Impossible de sauvegarder le dossier',
        variant: 'destructive',
      });
    }
  };

  const handleCVUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!file.name.toLowerCase().match(/\.(pdf|doc|docx)$/)) {
      toast({ title: 'Format invalide', description: 'Veuillez uploader un fichier PDF ou Word', variant: 'destructive' });
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast({ title: 'Fichier trop volumineux', description: 'La taille maximale autorisée est de 10 MB', variant: 'destructive' });
      return;
    }

    setUploadingCV(true);
    toast({ title: 'Upload en cours…', description: 'Le fichier est envoyé, l\'analyse démarrera dans quelques secondes.' });

    try {
      // Step 1: upload the file and get a job_id back immediately
      const uploadResult = await cvAPI.upload(file);

      // Step 2a: if server ran inline (no Redis), result is already here
      if (!uploadResult.async && uploadResult.result) {
        _applyParsedData(uploadResult.result);
        toast({ title: 'CV analysé avec succès !', description: 'Vérifiez et complétez les informations si nécessaire.' });
        event.target.value = '';
        return;
      }

      // Step 2b: poll until completed or failed (max 3 min, every 3s)
      const jobId = uploadResult.job_id;
      toast({ title: 'Analyse en cours…', description: 'L\'IA analyse le CV. Cela peut prendre jusqu\'à 2 minutes.' });

      const MAX_POLLS = 60;
      for (let i = 0; i < MAX_POLLS; i++) {
        await new Promise(r => setTimeout(r, 3000));
        const job = await cvAPI.getJob(jobId);

        if (job.status === 'completed') {
          _applyParsedData(job.result_payload);
          toast({ title: 'CV analysé avec succès !', description: 'Vérifiez et complétez les informations si nécessaire.' });
          event.target.value = '';
          return;
        }
        if (job.status === 'failed') {
          throw new Error(job.error_message || 'L\'analyse a échoué');
        }
        // still pending/processing — keep polling
      }
      throw new Error('L\'analyse a pris trop de temps. Veuillez réessayer.');

    } catch (error) {
      console.error('CV upload/parse error:', error);
      const msg = error?.response?.data?.detail || error.message || 'Impossible d\'analyser le CV';
      toast({ title: 'Erreur d\'analyse', description: msg, variant: 'destructive' });
    } finally {
      setUploadingCV(false);
    }
  };

  const _applyParsedData = (parsedData) => {
    setConsultantName(parsedData.consultant_name || parsedData.consultantName || '');
    setConsultantTitle(parsedData.consultant_title || parsedData.consultantTitle || '');
    setCompetences(competencesToText(parsedData.competences));
    if (parsedData.formations?.length > 0) {
      setFormations(parsedData.formations.map((f, idx) => ({ id: Date.now() + idx, ...f })));
    }
    if (parsedData.experiences?.length > 0) {
      setExperiences(parsedData.experiences.map((e, idx) => ({ id: Date.now() + idx, ...e })));
    }
  };

  const progressValue = (step / totalSteps) * 100;
  const stepLabels = ['1. Informations', '2. Compétences', '3. Formations', '4. Expériences'];
  const displayTemplates = templates.length > 0 ? templates : [
    { id: 'signal_azure', name: 'Signal Azure', description: 'Tech / SaaS moderne — azur électrique', thumbnail_colors: { primary: '#1E5AF5', accent: '#4DA3FF' } },
    { id: 'platinum_oxford', name: 'Platinum Oxford', description: 'Banque / PE — oxford blue et laiton', thumbnail_colors: { primary: '#0F2547', accent: '#c8a559' } },
  ];

  return (
    <AppLayout title={id === 'new' ? 'Nouveau Dossier' : 'Modifier le Dossier'}>
      <div className="df-wrap">
        {/* Progress bar */}
        <div className="df-progress-bar">
          <div className="df-steps">
            {stepLabels.map((label, i) => (
              <span key={i} className={`df-step-label ${i + 1 === step ? 'active' : i + 1 < step ? 'done' : ''}`}>
                {label}
              </span>
            ))}
          </div>
          <div className="df-progress-track">
            <div className="df-progress-fill" style={{ width: `${progressValue}%` }} />
          </div>
        </div>

        {/* Scrollable body */}
        <div className="df-body">

          {/* CV Upload — step 1 only */}
          {id === 'new' && step === 1 && (
            <div className="df-upload-box">
              <div className="df-upload-title">
                <Upload size={14} />
                Importer un CV
              </div>
              <div className="df-upload-desc">Gagnez du temps ! Uploadez votre CV (PDF ou Word) pour pré-remplir automatiquement le formulaire</div>
              <div className="df-upload-row">
                <input
                  type="file"
                  accept=".pdf,.doc,.docx"
                  onChange={handleCVUpload}
                  disabled={uploadingCV}
                  className="df-file-input"
                />
                <button
                  type="button"
                  className="df-btn df-btn-primary"
                  disabled={uploadingCV}
                  onClick={() => document.querySelector('.df-file-input').click()}
                  style={{ flexShrink: 0 }}
                >
                  {uploadingCV ? (
                    <><Loader2 size={13} className="animate-spin" /> Analyse…</>
                  ) : (
                    <><Upload size={13} /> Analyser</>
                  )}
                </button>
              </div>
              <div className="df-upload-hint">Formats acceptés : PDF, DOC, DOCX (max 10 MB)</div>
            </div>
          )}

          {/* Main card */}
          <div className="df-card">
            <div className="df-card-head">
              <div className="df-card-title">
                {step === 1 ? 'Informations Générales' : step === 2 ? 'Compétences' : step === 3 ? 'Formations' : 'Expériences Professionnelles'}
              </div>
              <div className="df-card-desc">
                {step === 1 ? 'Renseignez les informations du consultant' :
                 step === 2 ? 'Ajoutez les compétences techniques et professionnelles' :
                 step === 3 ? 'Détaillez les formations et certifications' :
                 'Listez les expériences professionnelles'}
              </div>
            </div>
            <div className="df-card-body">

              {/* ── Step 1 ── */}
              {step === 1 && (
                <>
                  <div className="df-field">
                    <label className="df-label">Nom du Consultant *</label>
                    <input className="df-input" placeholder="Jean Dupont" value={consultantName} onChange={e => setConsultantName(e.target.value)} />
                  </div>
                  <div className="df-field">
                    <label className="df-label">Poste / Titre *</label>
                    <input className="df-input" placeholder="Développeur Full Stack" value={consultantTitle} onChange={e => setConsultantTitle(e.target.value)} />
                  </div>

                  <div className="df-section-title">Personnalisation page de garde</div>

                  <div className="df-grid-2">
                    <div className="df-field">
                      <label className="df-label">Taille police du nom (pt)</label>
                      <select className="df-select" value={nameFontSize} onChange={e => setNameFontSize(e.target.value)}>
                        <option value="28">28 pt (Petit)</option>
                        <option value="32">32 pt</option>
                        <option value="36">36 pt (Normal)</option>
                        <option value="40">40 pt</option>
                        <option value="44">44 pt (Grand)</option>
                        <option value="48">48 pt (Très grand)</option>
                      </select>
                    </div>
                    <div className="df-field">
                      <label className="df-label">Taille police du titre (pt)</label>
                      <select className="df-select" value={titleFontSize} onChange={e => setTitleFontSize(e.target.value)}>
                        <option value="18">18 pt (Petit)</option>
                        <option value="20">20 pt</option>
                        <option value="22">22 pt</option>
                        <option value="24">24 pt (Normal)</option>
                        <option value="26">26 pt</option>
                        <option value="28">28 pt (Grand)</option>
                        <option value="32">32 pt (Très grand)</option>
                      </select>
                    </div>
                  </div>

                  <div className="df-field">
                    <label className="df-label">Type de police</label>
                    <select className="df-select" value={fontFamily} onChange={e => setFontFamily(e.target.value)}>
                      <option value="Helvetica-Bold">Helvetica Bold</option>
                      <option value="Helvetica">Helvetica</option>
                      <option value="Arial-Bold">Arial Bold</option>
                      <option value="Arial">Arial</option>
                      <option value="Calibri-Bold">Calibri Bold</option>
                      <option value="Calibri">Calibri</option>
                      <option value="Times-Bold">Times Bold (Sérif)</option>
                      <option value="Times-Roman">Times Roman (Sérif)</option>
                      <option value="Courier-Bold">Courier Bold (Monospace)</option>
                      <option value="Courier">Courier (Monospace)</option>
                    </select>
                  </div>

                  <div className="df-info-box">
                    <strong>Aperçu :</strong> Ces paramètres affectent uniquement la page de garde du PDF.
                  </div>

                  <div className="df-section-title" style={{ marginTop: 22 }}>Template de présentation</div>
                  <p style={{ fontSize: 12, color: 'var(--muted2)', marginBottom: 12 }}>Choisissez le style de votre dossier de compétences PDF</p>

                  <div className="df-tmpl-grid">
                    {displayTemplates.map(tmpl => (
                      <div
                        key={tmpl.id}
                        className={`df-tmpl-card ${templateId === tmpl.id ? 'selected' : ''}`}
                        onClick={() => setTemplateId(tmpl.id)}
                      >
                        <div
                          className="df-tmpl-thumb"
                          style={{ background: tmpl.thumbnail_colors?.primary || '#1e3a5f', borderLeft: `3px solid ${tmpl.thumbnail_colors?.accent || '#4a7aaa'}` }}
                        />
                        <div>
                          <div className="df-tmpl-name">{tmpl.name}</div>
                          <div className="df-tmpl-desc">{tmpl.description}</div>
                        </div>
                        {templateId === tmpl.id && (
                          <div className="df-tmpl-check">
                            <svg viewBox="0 0 12 12" width="9" height="9"><path d="M2 6l3 3 5-5" stroke="white" strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round"/></svg>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </>
              )}

              {/* ── Step 2 ── */}
              {step === 2 && (
                <>
                  <div className="df-field">
                    <label className="df-label">Compétences par domaine</label>
                    <p style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 6 }}>
                      Format : <code style={{color:'var(--vio2)'}}>Domaine: compétence1, compétence2</code> — une ligne par domaine.
                      Le CV importé remplit ce champ automatiquement.
                    </p>
                    <textarea
                      className="df-textarea mono"
                      rows={14}
                      placeholder={"Exemple:\nFinance de Marché: obligations convertibles, FCFE, CDO\nRisk Management: Value at Risk, Delta, Gamma, Vega\nMéthodologie: Scrum, Kanban, UML, Cycle en V\nOutils: SQL, SWIFT, FIXML"}
                      value={competences}
                      onChange={e => setCompetences(e.target.value)}
                    />
                  </div>
                </>
              )}

              {/* ── Step 3 ── */}
              {step === 3 && (
                <>
                  {formations.map((formation, index) => (
                    <div key={formation.id} className="df-sub-card">
                      <div className="df-sub-card-head">
                        <span className="df-sub-card-label">Formation {index + 1}</span>
                        {formations.length > 1 && (
                          <button className="df-btn df-btn-danger" onClick={() => removeFormation(formation.id)}>
                            <Trash2 size={12} /> Supprimer
                          </button>
                        )}
                      </div>
                      <div className="df-grid-2">
                        <div className="df-field">
                          <label className="df-label">Titre de la formation</label>
                          <input className="df-input" placeholder="Master Informatique" value={formation.title} onChange={e => updateFormation(formation.id, 'title', e.target.value)} />
                        </div>
                        <div className="df-field">
                          <label className="df-label">Établissement</label>
                          <input className="df-input" placeholder="Université de Paris" value={formation.institution} onChange={e => updateFormation(formation.id, 'institution', e.target.value)} />
                        </div>
                      </div>
                      <div className="df-field">
                        <label className="df-label">Année</label>
                        <input className="df-input" placeholder="2020" value={formation.year} onChange={e => updateFormation(formation.id, 'year', e.target.value)} style={{ maxWidth: 140 }} />
                      </div>
                      <div className="df-field">
                        <label className="df-label">Description <span className="df-label-hint">(optionnel)</span></label>
                        <textarea className="df-textarea" rows={2} placeholder="Décrivez la formation..." value={formation.description} onChange={e => updateFormation(formation.id, 'description', e.target.value)} />
                      </div>
                    </div>
                  ))}
                  <button className="df-btn-add" onClick={addFormation}>
                    <Plus size={13} /> Ajouter une formation
                  </button>
                </>
              )}

              {/* ── Step 4 ── */}
              {step === 4 && (
                <>
                  {experiences.map((experience, index) => (
                    <div key={experience.id} className="df-sub-card">
                      <div className="df-sub-card-head">
                        <span className="df-sub-card-label">Expérience {index + 1}</span>
                        {experiences.length > 1 && (
                          <button className="df-btn df-btn-danger" onClick={() => removeExperience(experience.id)}>
                            <Trash2 size={12} /> Supprimer
                          </button>
                        )}
                      </div>
                      <div className="df-grid-2">
                        <div className="df-field">
                          <label className="df-label">Intitulé du poste</label>
                          <input className="df-input" placeholder="Développeur Full Stack" value={experience.poste} onChange={e => updateExperience(experience.id, 'poste', e.target.value)} />
                        </div>
                        <div className="df-field">
                          <label className="df-label">Entreprise</label>
                          <input className="df-input" placeholder="TechCorp Solutions" value={experience.entreprise} onChange={e => updateExperience(experience.id, 'entreprise', e.target.value)} />
                        </div>
                      </div>
                      <div className="df-field">
                        <label className="df-label">Durée de mission</label>
                        <input className="df-input" placeholder="Janvier 2021 - Présent" value={experience.duree} onChange={e => updateExperience(experience.id, 'duree', e.target.value)} />
                      </div>
                      <div className="df-field">
                        <label className="df-label">Contexte <span className="df-label-hint">(optionnel)</span></label>
                        <textarea className="df-textarea" rows={2} placeholder="Décrivez le contexte de la mission (secteur, équipe, enjeux...)" value={experience.contexte || ''} onChange={e => updateExperience(experience.id, 'contexte', e.target.value)} />
                      </div>
                      <div className="df-field">
                        <label className="df-label">Mission / Description</label>
                        <textarea className="df-textarea" rows={4} placeholder={"Décrivez les missions et réalisations...\n(utilisez des tirets pour créer des puces)"} value={experience.mission} onChange={e => updateExperience(experience.id, 'mission', e.target.value)} />
                      </div>
                      <div className="df-field">
                        <label className="df-label">Environnement technique/fonctionnel <span className="df-label-hint">(optionnel)</span></label>
                        <textarea className="df-textarea" rows={2} placeholder="Technologies, outils, méthodologies utilisés..." value={experience.environnement || ''} onChange={e => updateExperience(experience.id, 'environnement', e.target.value)} />
                      </div>
                    </div>
                  ))}
                  <button className="df-btn-add" onClick={addExperience}>
                    <Plus size={13} /> Ajouter une expérience
                  </button>
                </>
              )}

            </div>
          </div>
        </div>

        {/* Bottom nav */}
        <div className="df-nav">
          <button
            className="df-btn df-btn-ghost"
            onClick={handlePrevious}
            disabled={step === 1}
          >
            <ArrowLeft size={13} /> Précédent
          </button>
          {step < totalSteps ? (
            <button className="df-btn df-btn-primary" onClick={handleNext}>
              Suivant <ArrowRight size={13} />
            </button>
          ) : (
            <button className="df-btn df-btn-save" onClick={handleSave}>
              <Save size={13} /> Enregistrer
            </button>
          )}
        </div>
      </div>
    </AppLayout>
  );
};

export default DossierForm;

